/** Court costs ledger — every petition fee, deposit, process-server / advocate invoice and costs order on the file, with totals and
 *  "Post to LoanDisk" for the recoverable, not-yet-posted entries (legal-fee slot on the loan). */
import { useEffect, useState } from "react";
import { Coins, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { addLegalCost, getLegalCosts, postLegalCostsToLoanDisk, voidLegalCost } from "@/lib/api";
import { Field, errMsg, fmtDate, money, today } from "./petitionShared";

function AddCostForm({ kinds, onAdd, busy }) {
  const [f, setF] = useState({ kind: "process_server", label: "", amount: "", incurred_on: today(), ref: "", recoverable: true, notes: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const valid = Number(f.amount) > 0 && !!f.incurred_on;
  return (
    <div className="border rounded p-2 bg-slate-50/60 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end" data-testid="cost-add-form">
      <Field label="Kind">
        <Select value={f.kind} onValueChange={set("kind")}>
          <SelectTrigger className="h-8 text-xs" data-testid="cost-kind"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(kinds || {}).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Label (optional)"><Input value={f.label} onChange={set("label")} placeholder="e.g. ABC Servers — SD2" className="h-8 text-xs" data-testid="cost-label" /></Field>
      <Field label="Amount £"><Input type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} className="h-8 text-xs" data-testid="cost-amount" /></Field>
      <Field label="Date"><Input type="date" value={f.incurred_on} max={today()} onChange={set("incurred_on")} className="h-8 text-xs" data-testid="cost-date" /></Field>
      <Field label="Invoice / ref"><Input value={f.ref} onChange={set("ref")} className="h-8 text-xs" data-testid="cost-ref" /></Field>
      <div className="flex items-center gap-2 pb-1">
        <label className="flex items-center gap-1 text-[11px]"><Checkbox checked={f.recoverable} onCheckedChange={(v) => setF((x) => ({ ...x, recoverable: !!v }))} data-testid="cost-recoverable" />Recoverable</label>
        <Button size="sm" className="h-8 text-[11px] bg-slate-800 hover:bg-slate-900" disabled={busy || !valid} onClick={async () => { await onAdd({ ...f, amount: Number(f.amount) }); setF((x) => ({ ...x, label: "", amount: "", ref: "", notes: "" })); }} data-testid="cost-add">
          <Plus className="w-3 h-3 mr-1" />Add
        </Button>
      </div>
    </div>
  );
}

export default function CourtCostsDialog({ caseRow, onClose, onChanged }) {
  const [st, setSt] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setSt(await getLegalCosts(caseRow.case_id)); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id]);

  const live = (st?.entries || []).filter((e) => !e.voided);
  const postable = live.filter((e) => e.recoverable && !e.posted_to_loandisk);
  const selAmount = postable.filter((e) => sel.includes(e.id)).reduce((a, e) => a + Number(e.amount || 0), 0);

  const wrap = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); setSt(r.state); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const post = async () => {
    if (!window.confirm(`Post ${money(selAmount)} to LoanDisk loan ${st.loandisk_loan_id || st.sf_number} as a legal fee? This changes the customer's balance in LoanDisk.`)) return;
    await wrap(() => postLegalCostsToLoanDisk(caseRow.case_id, sel), (r) => `${money(r.result.amount)} posted — LoanDisk legal fee ${money(r.result.fee_before)} → ${money(r.result.fee_after)}`);
    setSel([]);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="court-costs-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 flex-wrap"><Coins className="w-4 h-4 text-amber-700" />Court costs — {caseRow.customer_name}
            {st && <span className="text-[11px] font-normal text-muted-foreground">· {st.sf_number} · balance {money(st.balance)}</span>}</DialogTitle>
        </DialogHeader>
        {!st ? <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Reading the file…</div> : (
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cost-totals">
              {[["Total costs", st.totals.total, "cost-total"], ["Recoverable", st.totals.recoverable, "cost-recoverable-total"], ["Posted to LoanDisk", st.totals.posted, "cost-posted-total"], ["Not recoverable", st.totals.adverse, "cost-adverse-total"]].map(([l, v, id]) => (
                <div key={id} className="border rounded p-2 bg-slate-50/60"><div className="text-[10px] text-muted-foreground">{l}</div><div className="text-sm font-semibold" data-testid={id}>{money(v)}</div></div>
              ))}
            </div>
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="cost-table">
                <thead className="bg-slate-50 text-slate-500"><tr><th className="p-1.5 w-6"></th><th className="p-1.5 text-left">Date</th><th className="p-1.5 text-left">Cost</th><th className="p-1.5 text-left">Ref</th><th className="p-1.5 text-right">Amount</th><th className="p-1.5 text-left">Status</th><th className="p-1.5"></th></tr></thead>
                <tbody>
                  {live.length === 0 && <tr><td colSpan={7} className="p-3 text-center text-muted-foreground" data-testid="cost-empty">No court costs on this file yet — the petition fee and deposit are added automatically when the filing is recorded.</td></tr>}
                  {live.map((e) => {
                    const canPost = e.recoverable && !e.posted_to_loandisk;
                    return (
                      <tr key={e.id} className={cn("border-t", e.posted_to_loandisk && "bg-emerald-50/30")} data-testid={`cost-row-${e.id}`}>
                        <td className="p-1.5">{canPost && <Checkbox checked={sel.includes(e.id)} onCheckedChange={(v) => setSel((s) => (v ? [...s, e.id] : s.filter((x) => x !== e.id)))} data-testid={`cost-pick-${e.id}`} />}</td>
                        <td className="p-1.5 whitespace-nowrap">{fmtDate(e.incurred_on)}</td>
                        <td className="p-1.5"><div className="font-medium">{e.label}</div><div className="text-[10px] text-slate-500">{st.kinds[e.kind] || e.kind}{e.source === "auto" ? " · from the petition flow" : ""}{e.notes ? ` — ${e.notes}` : ""}</div></td>
                        <td className="p-1.5 text-slate-600">{e.ref || "—"}</td>
                        <td className="p-1.5 text-right font-medium whitespace-nowrap">{money(e.amount)}</td>
                        <td className="p-1.5 whitespace-nowrap">
                          {e.posted_to_loandisk ? <span className="text-emerald-700" title={`Loan ${e.posted_to_loandisk.loan_id} · fee ${money(e.posted_to_loandisk.fee_before)} → ${money(e.posted_to_loandisk.fee_after)}`}>Posted {fmtDate(e.posted_to_loandisk.at)}</span>
                            : e.recoverable ? <span className="text-amber-700">Recoverable · not posted</span> : <span className="text-slate-500">Not recoverable</span>}
                        </td>
                        <td className="p-1.5 text-right">
                          {!e.posted_to_loandisk && (
                            <button type="button" className="text-slate-400 hover:text-red-600" title="Remove" disabled={busy} data-testid={`cost-void-${e.id}`}
                                    onClick={() => { const r = window.prompt("Why is this cost being removed?"); if (r && r.trim().length >= 3) wrap(() => voidLegalCost(caseRow.case_id, e.id, r.trim()), "Cost removed"); }}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <AddCostForm kinds={st.kinds} busy={busy} onAdd={(p) => wrap(() => addLegalCost(caseRow.case_id, p), (r) => `${r.result.label} ${money(r.result.amount)} added`)} />
            <p className="text-[10px] text-muted-foreground">"Post to LoanDisk" raises the loan's legal-fee slot (fee id {st.legal_fee_id}) by the ticked recoverable costs so they sit on the customer's balance; each entry can be posted once. Costs orders against us are kept as not recoverable.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="court-costs-close">Close</Button>
          {postable.length > 0 && (
            <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={post} disabled={busy || sel.length === 0} data-testid="cost-post-loandisk">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}Post {sel.length ? money(selAmount) : "selected"} to LoanDisk
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
